<template>
  <transition name="crm-notice-fade">
    <div v-if="visible" class="crm-notice" :class="'crm-notice--' + type" role="alert">
      <div class="crm-notice__icon">
        <van-icon :name="iconName" />
      </div>
      <div class="crm-notice__msg">{{ message }}</div>
    </div>
  </transition>
</template>

<script>
/**
 * 全站统一提示（轻提示卡片）
 * ------------------------------------------------------------------
 * 统一标准：风格向主列表看齐 —— 圆角 10px、柔和阴影、主色 #2b5fd9、深灰 #1f2329 文字。
 * 替代散落的 Vant $toast / Element Message：两者样式不受控（Vant 黑底、Element 顶部红条），
 * 且后端 msg 较长时展示不全。本组件固定居中偏上、支持多行、失败必展示后端原文。
 *
 * 用法（组件内）：
 *   <CrmNotice ref="notice" />
 *   this.$refs.notice.show("保存成功");              // 默认 success
 *   this.$refs.notice.show(res.msg, "error");        // 失败：展示后端原文
 *   this.$refs.notice.ok("已删除");                   // 成功快捷
 *   this.$refs.notice.fail("请填写会议名称");          // 失败快捷
 *
 * props:
 *   duration  自动关闭毫秒数，默认 2200；传 0 表示不自动关闭
 */
export default {
  name: "CrmNotice",
  props: {
    duration: { type: Number, default: 2200 }
  },
  data() {
    return {
      visible: false,
      message: "",
      type: "success",
      _timer: null
    };
  },
  computed: {
    iconName() {
      const map = {
        success: "checked",
        error: "clear",
        warning: "warning-o",
        info: "info-o"
      };
      return map[this.type] || "checked";
    }
  },
  beforeDestroy() {
    if (this._timer) clearTimeout(this._timer);
  },
  methods: {
    /**
     * @param {String} message 提示文案（后端 msg 可直接透传，支持多行）
     * @param {String} type    success | error | warning | info
     * @param {Number} duration 覆盖 props 的显示时长
     */
    show(message, type, duration) {
      // 空文案不弹（避免出现一个空气泡）
      const msg = (message === null || message === undefined) ? "" : String(message).trim();
      if (!msg) return;
      this.message = msg;
      this.type = type || "success";
      this.visible = true;
      if (this._timer) clearTimeout(this._timer);
      const ms = (duration === undefined || duration === null) ? this.duration : Number(duration);
      if (ms > 0) {
        this._timer = setTimeout(() => { this.visible = false; }, ms);
      }
    },
    // 成功快捷
    ok(message, duration) {
      this.show(message || "操作成功", "success", duration);
    },
    // 失败快捷：后端 msg 优先，兜底文案可由调用方传入
    fail(message, duration) {
      this.show(message || "操作失败", "error", duration || 2800);
    }
  }
};
</script>

<style>
/* 全站唯一：轻提示样式。改这里 = 改全站提示观感。 */
.crm-notice {
  position: fixed;
  top: 92px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 82vw;
  padding: 12px 16px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 8px 28px rgba(15, 25, 50, 0.18);
  font-size: 14px;
  line-height: 1.55;
  color: #1f2329;
  word-break: break-all;
  pointer-events: none;
}
.crm-notice__icon {
  flex: none;
  font-size: 16px;
  line-height: 1.55;
}
.crm-notice__msg {
  flex: 1;
  min-width: 0;
}
/* 成功：主色系 */
.crm-notice--success .crm-notice__icon {
  color: #2b5fd9;
}
/* 失败：红（与 .confirm-dialog__btn--danger 同色系） */
.crm-notice--error .crm-notice__icon {
  color: #e2465b;
}
.crm-notice--warning .crm-notice__icon {
  color: #ed8b00;
}
.crm-notice--info .crm-notice__icon {
  color: #646a73;
}

.crm-notice-fade-enter-active,
.crm-notice-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.crm-notice-fade-enter,
.crm-notice-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-8px);
}
</style>



// WEBPACK FOOTER //
// src/components/CrmNotice.vue